/**
 * Every outbound Lettr link, in one place, so they all carry attribution.
 *
 * The UTM pair matches the register link the integration modal has always
 * used (`utm_source=topol&utm_medium=app`), so Lettr can tell one part of the
 * app from another by `utm_campaign` alone.
 */

export type LettrPurpose = 'campaign' | 'transactional';

const LETTR_HOME_URL = 'https://lettr.com';
const LETTR_APP_EMAILS_URL = 'https://app.lettr.com/emails';
const LETTR_APP_REGISTER_URL = 'https://app.lettr.com/register';
const INTEGRATION_DOCS_URL =
    'https://support.topol.io/en/articles/16960878-export-to-lettr';

const UTM_SOURCE = 'topol';
const UTM_MEDIUM = 'app';

const withUtm = (
    url: string,
    campaign: string,
    content?: string,
    params: Record<string, string> = {}
): string => {
    const parsed = new URL(url);

    Object.entries(params).forEach(([key, value]) => {
        parsed.searchParams.set(key, value);
    });

    parsed.searchParams.set('utm_source', UTM_SOURCE);
    parsed.searchParams.set('utm_medium', UTM_MEDIUM);
    parsed.searchParams.set('utm_campaign', campaign);

    if (content !== undefined) {
        parsed.searchParams.set('utm_content', content);
    }

    return parsed.toString();
};

/**
 * The Lettr list a just-exported template landed in. Lettr keeps campaign and
 * transactional templates apart, and its email list opens on transactional -
 * the campaign list has to be asked for by name.
 *
 * `source` names which export did it (a single template, or a bulk run) so the
 * two can be told apart in Lettr's analytics.
 */
export const lettrEmailsUrl = (
    purpose: LettrPurpose,
    source: 'single_export' | 'bulk_export'
): string =>
    withUtm(
        LETTR_APP_EMAILS_URL,
        'template_export',
        source,
        purpose === 'campaign' ? { purpose: 'campaign' } : {}
    );

/**
 * Lettr's marketing site, for surfaces where the reader has had one line of
 * pitch and no way to act on it inside Topol - plugin users have neither an
 * integrations page nor templates here, so there is nothing to connect. The
 * site carries the case and its own signup; `lettrRegisterUrl` stays for the
 * integration form, where the user has already decided.
 */
export const lettrHomeUrl = (campaign: string): string =>
    withUtm(LETTR_HOME_URL, campaign);

export const lettrRegisterUrl = (): string =>
    withUtm(LETTR_APP_REGISTER_URL, 'integration');

/**
 * How the export works, on Topol's own support site rather than Lettr's API
 * reference: the reader is connecting the integration, not calling the API.
 */
export const lettrIntegrationDocsUrl = (): string =>
    withUtm(INTEGRATION_DOCS_URL, 'integration_docs');
